import { useQuery } from "@tanstack/react-query";
import { Youtube, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { socialApi } from "@/lib/socialApi";
import YouTubeVideoCard from "@/components/public/YouTubeVideoCard";
import { SEO, breadcrumbJsonLd } from "@/lib/seo";

const VIDEOS_LIMIT = 15;
const CHANNEL_URL = 'https://www.youtube.com/channel/UC2h0v_PBUQzODLNT9EIUGzw';

const Videos = () => {
    const { t } = useTranslation();
    const { data, isLoading, isError } = useQuery({
        queryKey: ['youtube-videos', VIDEOS_LIMIT],
        queryFn: async () => {
            const res = await socialApi.getYouTubeVideos(VIDEOS_LIMIT);
            return res.data.data;
        },
        staleTime: 30 * 60 * 1000,
        retry: 1,
    });

    const videos = data?.videos ?? [];
    const channelUrl = data?.channelUrl ?? CHANNEL_URL;

    return (
        <>
            <SEO
                title={t("meta.videos_title")}
                description={t("meta.videos_description")}
                path="/videos"
                jsonLd={breadcrumbJsonLd([
                    { name: t("nav.home"), path: "/" },
                    { name: t("nav.videos"), path: "/videos" },
                ])}
            />

            <div className="flex flex-col">
                <section className="relative pt-32 sm:pt-36 md:pt-40 pb-12 sm:pb-16 md:pb-20 overflow-hidden">
                    <div className="container mx-auto px-6 relative z-10">
                        <div className="max-w-3xl">
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-white/60 mb-8">
                                <Youtube size={14} className="text-primary" />
                                {t("videos.header_badge")}
                            </div>
                            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-white mb-8 tracking-normal" style={{ lineHeight: '1.6' }}>
                                {t("videos.header_title")}
                            </h1>
                            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed font-medium mb-10">
                                {t("videos.header_subtitle")}
                            </p>
                            <a href={channelUrl} target="_blank" rel="noopener noreferrer">
                                <Button className="rounded-full bg-primary text-white font-bold gap-2 h-12 px-6">
                                    <Youtube size={18} />
                                    {t("videos.subscribe")}
                                </Button>
                            </a>
                        </div>
                    </div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[150px] pointer-events-none" />
                </section>

                <section className="pb-40">
                    <div className="container mx-auto px-6">
                        {isLoading && (
                            <div className="flex items-center justify-center py-32">
                                <Loader2 className="w-10 h-10 animate-spin text-primary" />
                            </div>
                        )}

                        {!isLoading && isError && (
                            <div className="text-center py-32">
                                <p className="text-xl text-muted-foreground font-medium mb-6">
                                    {t("videos.load_error")}
                                </p>
                                <a href={channelUrl} target="_blank" rel="noopener noreferrer">
                                    <Button variant="outline" className="rounded-full border-white/10 hover:bg-white/5 text-white font-bold gap-2">
                                        <Youtube size={18} />
                                        {t("videos.visit_channel")}
                                    </Button>
                                </a>
                            </div>
                        )}

                        {!isLoading && !isError && videos.length === 0 && (
                            <div className="text-center py-32">
                                <p className="text-xl text-muted-foreground font-medium">
                                    {t("videos.empty")}
                                </p>
                            </div>
                        )}

                        {!isLoading && !isError && videos.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                {videos.map((video) => (
                                    <YouTubeVideoCard key={video.id} video={video} />
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </>
    );
};

export default Videos;
